import { replace } from 'connected-react-router'
import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import {
  selectCreditCardStatisticsActiveBudgetCount,
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsBudgetCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Account/redux/account.selectors'
import {
  selectAccountResendVerificationError,
  selectAccountVerificationError,
  selectCreditCardIdFromRoute,
  selectGraphData,
  selectPostingAccountVerification,
  selectPostingResendAccountVerification,
  selectTotalSpent,
} from 'src/app/AccountOverview/redux/accountOverview.selectors'
import AccountOverviewBackgroundPageContent from 'src/components/AccountOverviewBackgroundPageContent/AccountOverviewBackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useBackgroundDispatch } from 'src/hooks'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { accountSettingsUrl, budgetOverviewUrl } from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
  useQueryCreditCards,
} from 'src/queries/creditCard.queries'
import { useQueryVirtualCards } from 'src/queries/virtualCards.queries'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import {
  getCreditCardPermissions,
  getParentCreditCard,
  postCreditCardStartVerificationThenOpenToast,
  postCreditCardVerifyThenOpenToast,
} from 'src/redux/actions/data/creditCards.actions'
import {
  selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed,
  selectIsUserOrglessRCNOrBudgetOwner,
} from 'src/redux/selectors/auth.selectors'

const AccountOverview: React.FC = () => {
  const dispatch = useBackgroundDispatch()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const {
    data: creditCard,
    isPending,
    isFetched: creditCardComplete,
    isError,
  } = useQueryCreditCard({
    creditCardId,
    enabled: !!creditCardId,
  })

  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId,
    retry: false,
    enabled: !!creditCardId,
  })

  const history = useHistory()

  const graphData = useSelector(selectGraphData)
  const totalSpent = useSelector(selectTotalSpent)
  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)
  const activeBudgetCount = useSelector(
    selectCreditCardStatisticsActiveBudgetCount,
  )
  const budgetCount = useSelector(selectCreditCardStatisticsBudgetCount)

  const postingAccountVerification = useSelector(
    selectPostingAccountVerification,
  )
  const accountVerificationError = useSelector(selectAccountVerificationError)
  const postingResendAccountVerification = useSelector(
    selectPostingResendAccountVerification,
  )
  const accountResendVerificationError = useSelector(
    selectAccountResendVerificationError,
  )

  const isUserOrglessRCNOrBudgetOwner = useSelector(
    selectIsUserOrglessRCNOrBudgetOwner,
  )
  const isConfirmRCNOrBudgetModalDismissed = useSelector(
    selectIsConfirmRCNOrBudgetModalAccountOverviewDismissed,
  )
  // REDIRECT EFFECT
  // If the user somehow comes onto this page and this RCN does not haev virtual card features,
  // Redirect them to the account settings page
  useEffect(() => {
    if (creditCard?.status === CreditCardStatus.NotApplicable) {
      history.replace(accountSettingsUrl(creditCard.id))
    }
  }, [creditCard, history])

  const {
    data: budgetsData,
    isLoading: creditCardsLoading,
    isFetched: creditCardsComplete,
  } = useQueryCreditCards({
    filters: {
      status: CreditCardStatus.Active,
      type: CreditCardType.Delegate,
      count: 3,
      page: 0,
      sortField: 'activeUpdatedAt',
      sortDirection: 'ASC',
      parentCreditCard: creditCardId,
    },
    // NOTE: If a credit card is physical only, we may want to disable this API
    // call for optimization. Holding off on this for now since it's not breaking anything
    // We can revisit this later
    enabled: !!creditCardId && !creditCard?.isBudget,
  })

  const {
    data: virtualCardsData,
    isLoading: virtualCardsLoading,
    isFetched: virtualCardsComplete,
  } = useQueryVirtualCards({
    filters: {
      statuses: [VirtualCardStatus.Active],
      creditCardId,
    },
    perPage: 8,
    enabled: !!creditCardId,
  })

  useEffect(() => {
    if (creditCard?.parentCreditCardId) {
      dispatch(getParentCreditCard(creditCard?.parentCreditCardId))
      dispatch(getCreditCardPermissions(creditCard?.parentCreditCardId))
    }
  }, [dispatch, creditCard?.parentCreditCardId])

  useEffect(() => {
    if (isUserOrglessRCNOrBudgetOwner && !isConfirmRCNOrBudgetModalDismissed) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true))
    }
  }, [
    dispatch,
    isUserOrglessRCNOrBudgetOwner,
    isConfirmRCNOrBudgetModalDismissed,
  ])

  const handleVerifyCreditCard = useCallback(
    (code: string): void => {
      dispatch(postCreditCardVerifyThenOpenToast(creditCardId, { code }))
    },
    [dispatch, creditCardId],
  )

  const handleResendVerification = useCallback((): void => {
    dispatch(postCreditCardStartVerificationThenOpenToast(creditCardId))
  }, [dispatch, creditCardId])

  const location = useLocation()
  useEffect(() => {
    if (!creditCard?.isBudget) return
    const target = budgetOverviewUrl(creditCardId)
    if (location.pathname !== target) {
      dispatch(replace(target))
    }
  }, [dispatch, creditCard?.isBudget, creditCardId, location.pathname])

  const isLoading =
    isPending ||
    !creditCardComplete ||
    !creditCardsComplete ||
    !virtualCardsComplete

  if (isLoading) return <LoadingBackgroundPage />
  if (isError) return <NotFoundBackgroundPage />
  if (!creditCard) return <NotFoundBackgroundPage />

  return (
    <AccountOverviewBackgroundPageContent
      creditCard={creditCard}
      creditCardPermissions={creditCardPermissions}
      activeCreditCardBudgets={budgetsData?.creditCards || []}
      activeVcnCount={activeVcnCount}
      vcnCount={vcnCount}
      activeBudgetCount={activeBudgetCount}
      budgetCount={budgetCount}
      activeCreditCardVirtualCards={virtualCardsData?.virtualCards || []}
      graphData={graphData}
      totalSpent={totalSpent}
      virtualCardsLoading={virtualCardsLoading}
      virtualCardsComplete={virtualCardsComplete}
      creditCardsLoading={creditCardsLoading}
      creditCardsComplete={creditCardsComplete}
      postingAccountVerification={postingAccountVerification}
      accountVerificationError={accountVerificationError}
      postingResendAccountVerification={postingResendAccountVerification}
      accountResendVerificationError={accountResendVerificationError}
      handleVerifyCreditCard={handleVerifyCreditCard}
      handleResendVerification={handleResendVerification}
    />
  )
}

export default AccountOverview
